/* @flow */
import * as React from 'react';
import { type Map } from 'immutable';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';
import cx from 'classnames';
import { summaryEntriesSelector, isJeLayoutRTLSelector } from 'domain/journal';
import { rtlEnable } from 'domain/env';
import { type EntriesType } from 'domain/journal/helper';
import { GridTable as Grid } from '../grid';
import { getMetaRange } from '../helpers';
import { getDataCell, isActive, isMandatory } from './helpers';
import { renderColgroup } from './component';
import { alpha } from '@mui/material';

type Props = {|
  classes: {|
    [key: string]: string,
  |},
  data: Map<string, EntriesType>,
  getItem: (d: ?EntriesType) => React$Node,
  active: string,
  isJeLayoutRTL: boolean,
  rtl: boolean,
|};

const sheet = (theme) => ({
  container: {
    marginTop: 20,
    paddingLeft: 30,
    position: 'relative',
    marginBottom: 60, // this allow summary to be scrolled to bottom
  },
  wrapper: {
    border: '1px solid #F0F0F0',
    borderTop: `2px solid ${theme.palette.primary.main}`,
    position: 'relative',
    padding: [15, 20],
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  tableRemove: {
    width: 30,
  },
  column: {
    width: '25%',
  },
  row: {
    backgroundColor: 'transparent',
    '&:last-child $col:after': {
      display: 'none',
    },
  },
  col: {
    '& [data-element="je.fakeCell.value"]': {
      borderBottom: '1px solid #DEE2E6',
      paddingLeft: 0,
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      display: 'block',
      top: '100%',
      left: 0,
      right: 0,
      borderBottom: '1px solid #DEE2E6',
    },
    '&:nth-child(2)': {
      textAlign: 'right',
      paddingRight: 10,
      '&:after': {
        right: 10,
      },
    },
    '&:nth-child(4)': {
      textAlign: 'right',
    },
  },
  mandatory: {
    '& [data-element="je.fakeCell.value"]': {
      borderBottomColor: theme.palette.error.main,
    },
  },
  active: {
    '& [data-element="je.fakeCell.value"], & [data-element="je.autocomplete.field"]': {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    '& [data-element="je.hintinput.field"], & [data-element="je.literal.field"] ': {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      padding: 0,
    },
  },
});

const DEFAULT_COLUMN = ['A', 'B', 'C', 'D'];

class SettingsLayout extends React.Component<Props> {
  get isRevers() {
    return this.props.isJeLayoutRTL && this.props.rtl;
  }

  get meta() {
    const defColumn = this.isRevers ? DEFAULT_COLUMN.slice().reverse() : DEFAULT_COLUMN;
    const [column, row] = getMetaRange(this.props.data, this.isRevers);
    return [column.length < 4 ? defColumn : column, row];
  }

  getCollStyle = (col: string, row: number) => {
    const { classes, active } = this.props;
    const entry = this.getData(row, col);
    return cx(classes.col, {
      [classes.mandatory]: isMandatory(entry),
      [classes.active]: isActive(col, row, active),
    });
  };

  getData = (row: number, col: string) => getDataCell(this.props.data, row, col);

  getItem = (row: number, col: string) => this.props.getItem(this.getData(row, col));

  getRowStyle = () => {
    const { classes } = this.props;
    return classes.row;
  };

  colgroup = () => {
    const { classes } = this.props;
    return renderColgroup([{ id: 'A', className: classes.column, span: this.meta[0].length }]);
  };

  render() {
    const { classes } = this.props;
    return (
      <div className={classes.container}>
        <div className={classes.wrapper}>
          <Grid
            meta={this.meta}
            getRowStyle={this.getRowStyle}
            getCollStyle={this.getCollStyle}
            getItem={this.getItem}
            colgroup={this.colgroup}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  data: summaryEntriesSelector(state),
  isJeLayoutRTL: isJeLayoutRTLSelector(state),
  rtl: rtlEnable(state),
});

export default compose(connect(mapStateToProps), withStyles(sheet, { withTheme: true }))(SettingsLayout);
